import React, { useState, useEffect } from 'react';
import { 
  Typography, 
  Box, 
  Grid, 
  Card, 
  CardContent, 
  CardMedia, 
  Chip, 
  Button,
  Tabs,
  Tab,
  Container,
  Avatar,
  Rating,
  Divider,
  Snackbar,
  Alert
} from '@mui/material';
import { 
  LocationOn, 
  Assignment, 
  Store, 
  Star,
  People,
  AccessTime,
  TrendingUp
} from '@mui/icons-material';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Campaign, campaignData, categoryInfo } from '../data/campaignData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`category-tabpanel-${index}`}
      aria-labelledby={`category-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: { xs: 1, sm: 2 } }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const CategoryPage: React.FC = () => {
  const { category = 'area' } = useParams<{ category: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { currentUser } = useAuth();
  
  const [snackbar, setSnackbar] = useState<{
    open: boolean;
    message: string;
    severity: 'success' | 'error' | 'info';
  }>({
    open: false,
    message: '',
    severity: 'info'
  });

  // URL 파라미터에 따른 탭 값 설정
  const getTabValue = (categoryParam: string) => {
    switch (categoryParam) {
      case 'area': return 0;
      case 'report': return 1;
      case 'store': return 2;
      default: return 0;
    }
  };

  const [tabValue, setTabValue] = useState(getTabValue(category));

  // URL 파라미터가 변경될 때 탭 값 업데이트
  useEffect(() => {
    setTabValue(getTabValue(category));
  }, [category]);

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
    
    // 탭 변경 시 URL 업데이트
    const categoryMap = ['area', 'report', 'store'];
    const newCategory = categoryMap[newValue];
    navigate(`/category/${newCategory}`, { replace: true });
  };

  const handleParticipate = (campaignId: string, campaignTitle: string) => {
    // AuthContext에서 실제 로그인 상태 확인
    if (!currentUser) {
      setSnackbar({
        open: true,
        message: '로그인이 필요한 서비스입니다.',
        severity: 'error'
      });
      return;
    }

    // 캠페인 상세 페이지로 이동
    navigate(`/campaign/${campaignId}`, { 
      state: { 
        campaignTitle,
        fromCategory: true 
      } 
    });
  };

  const handleCloseSnackbar = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderCampaignCard = (campaign: Campaign, type: string) => (
    <Card 
      sx={{ 
        height: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        transition: 'all 0.3s ease-in-out',
        cursor: 'pointer',
        '&:hover': {
          transform: 'translateY(-8px)',
          boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
          '& .campaign-image': {
            transform: 'scale(1.05)'
          }
        }
      }}
      onClick={() => handleParticipate(campaign.id, campaign.title)}
    >
      <Box sx={{ overflow: 'hidden' }}>
        <CardMedia
          component="img"
          height="200"
          image={campaign.imageUrl}
          alt={campaign.title}
          className="campaign-image"
          sx={{ 
            height: { xs: 160, sm: 200 },
            objectFit: 'cover',
            transition: 'transform 0.3s ease-in-out'
          }}
        />
      </Box>
      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          {type === 'area' && <LocationOn color="primary" sx={{ mr: 1 }} />}
          {type === 'report' && <Assignment color="secondary" sx={{ mr: 1 }} />}
          {type === 'store' && <Store color="success" sx={{ mr: 1 }} />}
          <Chip 
            label={type === 'area' ? campaign.area : type === 'report' ? campaign.category : campaign.store}
            size="small"
            color={type === 'area' ? 'primary' : type === 'report' ? 'secondary' : 'success'}
            variant="outlined"
            sx={{ fontWeight: 'bold' }}
          />
        </Box>
        
        <Typography variant="h6" component="h3" gutterBottom sx={{ fontWeight: 'bold', lineHeight: 1.3 }}>
          {campaign.title}
        </Typography>
        
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2, flexGrow: 1, lineHeight: 1.5 }}>
          {campaign.description}
        </Typography>

        <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
          <Rating value={campaign.rating} precision={0.1} size="small" readOnly />
          <Typography variant="body2" sx={{ ml: 1, fontWeight: 'bold' }}>
            {campaign.rating} ({campaign.reviewCount})
          </Typography>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <People sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {campaign.participants}/{campaign.maxParticipants}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <AccessTime sx={{ fontSize: 16, mr: 0.5, color: 'text.secondary' }} />
            <Typography variant="body2" color="text.secondary">
              {campaign.deadline}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <Typography variant="h6" color="primary" sx={{ fontWeight: 'bold' }}>
            {campaign.reward}P
          </Typography>
          <Button 
            variant="contained" 
            size="small"
            onClick={(e) => {
              e.stopPropagation();
              handleParticipate(campaign.id, campaign.title);
            }}
            sx={{ 
              borderRadius: 2,
              textTransform: 'none',
              fontWeight: 'bold',
              px: 2,
              py: 0.5,
              '&:hover': {
                transform: 'scale(1.05)'
              }
            }}
          >
            참여하기
          </Button>
        </Box>
      </CardContent>
    </Card>
  );

  return (
    <Container sx={{ px: { xs: 2, sm: 3 } }}>
      <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: { xs: 2, sm: 3 } }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="category tabs"
          variant="fullWidth"
          sx={{
            '& .MuiTab-root': {
              textTransform: 'none',
              fontWeight: 'bold',
              fontSize: { xs: '0.9rem', sm: '1.1rem' },
              minHeight: { xs: 56, sm: 64 },
              px: { xs: 1, sm: 2 },
              '&:hover': {
                backgroundColor: 'rgba(0,0,0,0.04)'
              }
            },
            '& .Mui-selected': {
              color: 'primary.main'
            }
          }}
        >
          <Tab 
            icon={<LocationOn />} 
            label="지역별" 
            iconPosition="start"
          />
          <Tab 
            icon={<Assignment />} 
            label="리포트" 
            iconPosition="start"
          />
          <Tab 
            icon={<Store />} 
            label="스토어리뷰" 
            iconPosition="start"
          />
        </Tabs>
      </Box>

      <TabPanel value={tabValue} index={0}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {campaignData.area.map((campaign) => (
            <Grid item xs={12} sm={6} md={4} key={campaign.id}>
              {renderCampaignCard(campaign, 'area')}
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={1}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {campaignData.report.map((campaign) => (
            <Grid item xs={12} sm={6} md={4} key={campaign.id}>
              {renderCampaignCard(campaign, 'report')}
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <TabPanel value={tabValue} index={2}>
        <Grid container spacing={{ xs: 2, sm: 3 }}>
          {campaignData.store.map((campaign) => (
            <Grid item xs={12} sm={6} md={4} key={campaign.id}>
              {renderCampaignCard(campaign, 'store')}
            </Grid>
          ))}
        </Grid>
      </TabPanel>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert 
          onClose={handleCloseSnackbar} 
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default CategoryPage; 